import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { getEmployeeProfile } from '@/lib/employeeStorage';
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Calendar,
  Award,
  TrendingUp,
  Star,
  Shield,
  Loader2
} from 'lucide-react';

export default function EmployeeProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { allUsers } = useAuth();
  const { pitches } = useData();
  const [imageError, setImageError] = useState(false);
  const [profile, setProfile] = useState<any>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  const employee = allUsers.find(u => u.id === id);
  
  // Load profile asynchronously
  useEffect(() => {
    const loadProfile = async () => {
      if (employee) {
        setLoadingProfile(true);
        try {
          const profileData = await getEmployeeProfile(employee.email, employee.role);
          setProfile(profileData);
        } catch (error) {
          console.error('Error loading profile:', error);
        } finally {
          setLoadingProfile(false);
        }
      } else {
        setLoadingProfile(false);
      }
    };
    
    loadProfile();
  }, [employee]);

  if (!employee) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Employee not found.</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/admin/users')}>
          Back to Directory
        </Button>
      </div>
    );
  }

  if (loadingProfile) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Calculate performance metrics
  const assignedPitches = pitches.filter(p => p.assignedAnalystId === employee.id);
  const reviewedPitches = pitches.filter(p => p.analysisScores && p.assignedAnalystId === employee.id);
  const avgScore = reviewedPitches.length > 0 
    ? Math.round(reviewedPitches.reduce((sum, p) => sum + (p.analysisScores?.overall || 0), 0) / reviewedPitches.length)
    : 0;

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getInitialsBgColor = (role: string) => {
    switch(role) {
      case 'senior_analyst':
        return 'bg-purple-600 text-white';
      case 'analyst':
        return 'bg-blue-600 text-white';
      case 'founder':
        return 'bg-green-600 text-white';
      default:
        return 'bg-gray-600 text-white';
    }
  };

  // Build experience from profile
  const experience = [
    { role: profile?.title || 'Analyst', company: 'Vett & Venture', period: `${profile?.startDate || 'Present'} - Present`, description: profile?.bio || '' }
  ];

  return (
    <div className="space-y-6 max-w-6xl mx-auto px-4 py-6">
      <Button variant="ghost" onClick={() => navigate('/admin/users')} className="gap-2">
        <ArrowLeft className="h-4 w-4" />
        Back to Directory
      </Button>

      <Card className="overflow-hidden">
        <div className="h-32 bg-gradient-to-r from-blue-600 to-purple-600" />
        
        <CardContent className="relative pt-0">
          <div className="flex flex-col md:flex-row gap-8 -mt-16">
            <div className="flex-shrink-0">
              <div className="h-32 w-32 rounded-full overflow-hidden border-4 border-white shadow-xl bg-white">
                {profile?.imageUrl && !imageError ? (
                  <img 
                    src={profile.imageUrl}
                    alt={employee.name}
                    className="h-full w-full object-cover"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <div className={`h-full w-full flex items-center justify-center text-3xl font-bold ${getInitialsBgColor(employee.role)}`}>
                    {getInitials(employee.name)}
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex-1 pt-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h1 className="text-3xl font-bold">{employee.name}</h1>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-lg text-muted-foreground">{profile?.title}</span>
                    <span className="text-muted-foreground">•</span>
                    <span className="text-muted-foreground flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      {profile?.location}
                    </span>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Button variant="outline" disabled>Message</Button>
                  <Button variant="outline" onClick={() => navigate(`/employee/${id}/access`)}>
                    <Shield className="h-4 w-4 mr-2" />
                    Access
                  </Button>
                  <Button onClick={() => navigate(`/employee/${id}/edit`)}>
                    Edit Profile
                  </Button>
                </div>
              </div>

              <p className="mt-4 text-muted-foreground">{profile?.bio}</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-medium">{employee.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Phone</p>
                    <p className="font-medium">{profile?.phone}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Location</p>
                    <p className="font-medium">{profile?.location}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Joined</p>
                    <p className="font-medium">{profile?.startDate}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="experience">Experience</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="pitches">Pitches</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="h-5 w-5" />
                  Education
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="whitespace-pre-line">{profile?.education}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="h-5 w-5" />
                  Specializations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {profile?.specialization?.map((spec: string, i: number) => (
                    <Badge key={i} variant="secondary">{spec}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Key Metrics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-muted/30 rounded-lg">
                  <p className="text-2xl font-bold">{profile?.yearsExperience || 0}</p>
                  <p className="text-sm text-muted-foreground">Years Experience</p>
                </div>
                <div className="text-center p-4 bg-muted/30 rounded-lg">
                  <p className="text-2xl font-bold">{assignedPitches.length}</p>
                  <p className="text-sm text-muted-foreground">Pitches Analyzed</p>
                </div>
                <div className="text-center p-4 bg-muted/30 rounded-lg">
                  <p className="text-2xl font-bold">{avgScore}</p>
                  <p className="text-sm text-muted-foreground">Avg Score</p>
                </div>
                <div className="text-center p-4 bg-muted/30 rounded-lg">
                  <p className="text-2xl font-bold">
                    {assignedPitches.length > 0 
                      ? Math.round((assignedPitches.filter(p => p.stage === 'funding').length / assignedPitches.length) * 100)
                      : 0}%
                  </p>
                  <p className="text-sm text-muted-foreground">Success Rate</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="experience">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5" />
                Work Experience
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {experience.map((exp: any, i: number) => (
                <div key={i}>
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold text-lg">{exp.role}</h3>
                      <p className="text-muted-foreground">{exp.company}</p>
                    </div>
                    <Badge variant="outline">{exp.period}</Badge>
                  </div>
                  <p className="mt-2 text-muted-foreground">{exp.description}</p>
                  {i < experience.length - 1 && <Separator className="mt-4" />}
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performance">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Performance Metrics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="text-center p-4 bg-muted/30 rounded-lg">
                  <p className="text-3xl font-bold">{assignedPitches.length}</p>
                  <p className="text-sm text-muted-foreground">Pitches Reviewed</p>
                </div>
                <div className="text-center p-4 bg-muted/30 rounded-lg">
                  <p className="text-3xl font-bold">{avgScore}</p>
                  <p className="text-sm text-muted-foreground">Average Score</p>
                </div>
                <div className="text-center p-4 bg-muted/30 rounded-lg">
                  <p className="text-3xl font-bold">
                    {assignedPitches.length > 0 
                      ? Math.round((assignedPitches.filter(p => p.stage === 'funding').length / assignedPitches.length) * 100)
                      : 0}%
                  </p>
                  <p className="text-sm text-muted-foreground">Success Rate</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-2">
                    <span>Review Accuracy</span>
                    <span>{avgScore}%</span>
                  </div>
                  <Progress value={avgScore} className="h-2" />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pitches">
          <Card>
            <CardHeader>
              <CardTitle>Assigned Pitches</CardTitle>
            </CardHeader>
            <CardContent>
              {assignedPitches.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No pitches assigned</p>
              ) : (
                <div className="space-y-4">
                  {assignedPitches.map((pitch) => (
                    <div 
                      key={pitch.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 cursor-pointer"
                      onClick={() => navigate(`/pitch/${pitch.id}`)}
                    >
                      <div>
                        <p className="font-medium">{pitch.companyName}</p>
                        <p className="text-sm text-muted-foreground">{pitch.tagline}</p>
                      </div>
                      <Badge>{pitch.stage}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}